/**
 * "My datasets" bar for PlotShell — signed-in users can save the pasted
 * dataset under a name, reload it later, and get their last input restored
 * automatically. Everything is stored locally in the browser vault
 * (IndexedDB), keyed per user and per chart tool.
 */

import { Link, useLocation } from "@tanstack/react-router";
import { LogIn, Save, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useVaultRecord } from "@/features/vault/use-vault-record";
import { m } from "@/paraglide/messages";
import {
  addDataset,
  type ChartDatasetVault,
  defaultDatasetName,
  removeDataset,
  withLastText,
} from "./chart-datasets";

interface PlotDatasetsProps {
  /** Tool registry id, e.g. "bar-chart" — namespaces the saved datasets. */
  toolId: string;
  /** Current textarea content. */
  text: string;
  /** The tool's preset example (never auto-saved or auto-restored over). */
  example: string;
  /** Load a dataset into the textarea. */
  onLoad: (text: string) => void;
}

export function PlotDatasets({
  toolId,
  text,
  example,
  onLoad,
}: PlotDatasetsProps) {
  const { pathname } = useLocation();
  const record = useVaultRecord<ChartDatasetVault>("chart-datasets", toolId);
  const { signedIn, isLoaded, value, save } = record;

  const [naming, setNaming] = useState(false);
  const [name, setName] = useState("");

  // Focus the name input when it is revealed (user-initiated, not a
  // page-load autofocus).
  const nameInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (naming) nameInputRef.current?.focus();
  }, [naming]);

  // Restore the last input once per mount — only while the textarea still
  // shows the untouched example, so user edits are never clobbered.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || !signedIn || !isLoaded) return;
    restoredRef.current = true;
    const lastText = value?.lastText;
    if (lastText && lastText !== example && text === example) {
      onLoad(lastText);
    }
  }, [signedIn, isLoaded, value, example, text, onLoad]);

  // Auto-remember the current input (debounced). The pristine example is
  // not worth remembering.
  useEffect(() => {
    if (!signedIn || !isLoaded || !restoredRef.current) return;
    if (text === example || text === (value?.lastText ?? "")) return;
    const timer = setTimeout(
      () => save(withLastText(value ?? null, text)),
      800,
    );
    return () => clearTimeout(timer);
  }, [signedIn, isLoaded, text, example, value, save]);

  if (!signedIn) {
    return (
      <Link
        to="/login"
        search={{ redirectUrl: pathname }}
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <LogIn className="h-3 w-3" aria-hidden="true" />
        {m.sci_plot_signin_hint()}
      </Link>
    );
  }

  const datasets = value?.datasets ?? [];

  const handleSave = () => {
    save(addDataset(value ?? null, name, text));
    setName("");
    setNaming(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {datasets.map((d) => (
        <span
          key={d.id}
          className="sci-chip inline-flex items-center overflow-hidden rounded-md text-xs"
        >
          <button
            type="button"
            onClick={() => onLoad(d.text)}
            className="max-w-40 truncate px-2 py-1 transition-opacity hover:opacity-80"
          >
            {d.name}
          </button>
          <button
            type="button"
            onClick={() => value && save(removeDataset(value, d.id))}
            aria-label={m.sci_plot_delete_dataset({ name: d.name })}
            className="px-1 py-1 opacity-60 transition-opacity hover:opacity-100"
          >
            <X className="h-3 w-3" aria-hidden="true" />
          </button>
        </span>
      ))}

      {naming ? (
        <span className="inline-flex items-center gap-1">
          <input
            type="text"
            value={name}
            ref={nameInputRef}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
              if (e.key === "Escape") setNaming(false);
            }}
            placeholder={defaultDatasetName(value ?? null)}
            aria-label={m.sci_plot_dataset_name()}
            className="h-6 w-32 rounded-md border border-border bg-background px-2 text-xs text-foreground placeholder:text-muted-foreground"
          />
          <button
            type="button"
            onClick={handleSave}
            className="sci-chip rounded-md px-2 py-1 text-xs transition-opacity hover:opacity-80"
          >
            {m.sci_plot_save()}
          </button>
          <button
            type="button"
            onClick={() => setNaming(false)}
            className="rounded-md px-1.5 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {m.sci_plot_cancel()}
          </button>
        </span>
      ) : (
        <button
          type="button"
          onClick={() => setNaming(true)}
          className="sci-chip inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-opacity hover:opacity-80"
        >
          <Save className="h-3 w-3" aria-hidden="true" />
          {m.sci_plot_save_dataset()}
        </button>
      )}
    </div>
  );
}
